import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/apiService';
import { pickReadableText, pickPageBackgroundText, mixHex } from '../theme';

const ThemeContext = createContext(null);

function hexToRgbTriple(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return null;
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

// Sets the CSS custom properties theme.js's var() references read from.
// Imperative (documentElement.style.setProperty) rather than a stylesheet —
// this codebase has no CSS files, and this keeps that consistent.
function applyBranding(branding) {
  const root = document.documentElement.style;
  const set = (name, hex) => {
    if (!hex) return;
    root.setProperty(name, hex);
    const rgb = hexToRgbTriple(hex);
    if (rgb) root.setProperty(`${name}-rgb`, rgb);
  };
  set('--primary-color', branding?.primaryColor);
  set('--secondary-color', branding?.secondaryColor);
  set('--accent-color', branding?.accentColor);
  set('--tile-background', branding?.tileBackground);
  set('--tile-border', branding?.tileBorder);
  set('--sidebar-background', branding?.sidebarBackground);
  set('--sidebar-text', branding?.sidebarText);
  set('--shell-background', branding?.shellBackground);
  set('--shell-accent', branding?.shellBackgroundAccent);
  const gradientAccent = branding?.shellBackgroundAccent || branding?.accentColor;
  const shellBlend = mixHex(branding?.shellBackground, gradientAccent);
  if (shellBlend) root.setProperty('--shell-background-blend', shellBlend);

  const setTextOn = (name, bgHex) => {
    const text = pickReadableText(bgHex);
    if (text) root.setProperty(name, text);
  };
  setTextOn('--text-on-primary', branding?.primaryColor);
  setTextOn('--text-on-tile-background', branding?.tileBackground);
  setTextOn('--text-on-sidebar-background', branding?.sidebarBackground);

  // The page background is a two-stop primary/accent gradient, not a single
  // color — pickPageBackgroundText approximates it as their midpoint.
  const pageText = pickPageBackgroundText(branding?.primaryColor, branding?.accentColor);
  if (pageText) root.setProperty('--text-on-page-background', pageText);
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [effective, setEffective] = useState(null);

  // Fetches the merged theme (tenant defaults + this user's personal
  // overrides, precedence resolved server-side) and applies it. Called at
  // bootstrap, and again after either layer is saved from CustomizationPage.
  const refreshBranding = useCallback(async () => {
    try {
      const data = await apiService.getMyTheme();
      applyBranding(data?.effective);
      setEffective(data?.effective || {});
    } catch (err) {
      console.error('Failed to load theme:', err);
    }
  }, []);

  // Loaded once per session, right after auth resolves who the tenant is —
  // same bootstrap point AuthContext becomes aware of user.businessId.
  useEffect(() => {
    if (user) refreshBranding();
  }, [user, refreshBranding]);

  return (
    <ThemeContext.Provider value={{ effective, refreshBranding }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
